import { getPatientLastAppointment } from "@/Common/Api/Appointment/CommonAppointmentApiClient";
import { Client } from "@/Common/Api/Client";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { ClinicSettings } from "@/Common/Api/MedfarDictionaries/Dto/Responses/ClinicSettings";
import { PatientBannerUpcomingAppointment } from "@/Common/Components/Appointment/PatientBannerUpcomingAppointment";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { HyperLinkRedirectControl, HyperLinkRedirectControlPatientProfileSubTabs } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { ClickToDialViewMode } from "@/Common/Components/CallCenter/ClickToDial.types";
import { ClickToDialViewPhoneNumber } from "@/Common/Components/CallCenter/ClickToDialViewPhoneNumber";
import { BritishColumbiaPatientBannerMspCoverage } from "@/Common/Components/Patient/HealthCoverageStatus/BritishColumbiaPatientBannerMspCoverage";
import { PatientBannerDropdown } from "@/Common/Components/Patient/PatientBannerDropdown";
import { PatientBannerHealthCoverage } from "@/Common/Components/Patient/PatientBannerHealthCoverage";
import { PreferredPharmacyStatus } from "@/Common/Components/Patient/PreferredPharmacyStatus";
import { PopulationBasedFundingRegistrationPopup } from "@/Common/Components/PopulationBasedFunding/PopulationBasedFundingRegistrationPopup";
import { PopulationBasedFundingStatusController } from "@/Common/Components/PopulationBasedFunding/PopulationBasedFundingStatusController";
import { UpcomingAppointment } from "@/Common/Models/Appointments/UpcomingAppointment";
import { Address } from "@/Common/Models/Contacts/Address";
import { Patient } from "@/Common/Models/Patient/Patient";
import { OntarioPatientEnrollmentModel } from "@/Common/Models/Patient/PatientEnrollment/Ontario/OntarioPatientEnrollmentModel";
import { ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { Person } from "@/Common/Models/Person/Person";
import { PopulationBasedFundingFormData } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingFormData";
import { RedirectTo } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { PopulationBasedFundingActions } from "@/Common/Services/PopulationBasedFunding/PopulationBasedFundingService";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { PhoneNumberHelper } from "@/ContactDirectory/Helpers/PhoneNumberHelper";
import { IconBadge } from "@/DesignSystem/Molecules/IconBadge/IconBadge";

import "./PatientBannerForLabs.css";

import img_p_banner_icon_100 from "@/Globals/_images/p-banner-icon-100.png";

interface IPatientBannerForLabsProps {
    Model: IPatientBannerModel;
    shouldLoadLastUpcomingAppointment?: boolean;
    shouldHideMemo?: boolean;
    shouldHideChangePicture?: boolean;
    shouldHideActivationLabel?: boolean;
    shouldHideUpdateInformation?: boolean;
    onTaskOrMessageClick?: (patient: Person, isTask: boolean) => void;
    onNewExternalCommunicationClick?: () => void;
    onDropDownMenuClick?: () => void;
    isCurrentEmployeeActiveSiteInBritishColumbia?: boolean;
}

interface IPatientBannerForLabsState {
    nextUpcomingAppointment: UpcomingAppointment;
    lastUpcomingAppointment: UpcomingAppointment;
    showPopulationBasedFundingRegistrationPopup: boolean;
}

class PatientBannerForLabs extends BaseReactComponent<IPatientBannerForLabsProps, IPatientBannerForLabsState> {
    constructor(props: IPatientBannerForLabsProps) {
        super(props);

        this.state = {
            nextUpcomingAppointment: undefined,
            showPopulationBasedFundingRegistrationPopup: undefined,
            lastUpcomingAppointment: null
        };
    }

    public componentDidMount(): void {
        if (cacheStore.ClinicSettings.EnableOntarioPatientEnrollment && sessionStore.getCurrentEmployee().getActiveSite()?.isSiteInProvince(CanadaState.Ontario))
            this.getOntarioEnrollmentStatus();

        this.loadUpcomingAppointments();
    }

    public componentDidUpdate(prevProps: Readonly<IPatientBannerForLabsProps>): void {
        const previousIndividualResponseModelContactFacetId = prevProps.Model?.get("Model")?.IndividualResponse?.ContactFacetId;
        const currentIndividualResponseModelContactFacetId = this.props.Model?.get("Model")?.IndividualResponse?.ContactFacetId;
        if (previousIndividualResponseModelContactFacetId !== currentIndividualResponseModelContactFacetId) {
            this.loadUpcomingAppointments();
        }
    }

    public render(): JSX.Element {
        let patient = this.props.Model.get("Model");
        return this.renderHorizontal(patient);
    }

    private renderHorizontal(patient: Patient): JSX.Element {
        if (patient == null) {
            console.error("patient is null in PatientBannerForLabs control");
        }
        // Last and upcoming appointment
        const lastAppointmentDisplay: JSX.Element = this.props.shouldLoadLastUpcomingAppointment ? (
            <PatientBannerUpcomingAppointment
                appointment={this.state.lastUpcomingAppointment}
                label={cacheStore.GetString("HeaderLastVisitLabel")}
            />
        ) : null;
        const upcomingAppointmentDisplay: JSX.Element = (
            <PatientBannerUpcomingAppointment
                appointment={this.state.nextUpcomingAppointment}
                label={cacheStore.GetString("HeaderNextVisitLabel")}
            />
        );

        // phone numbers display
        let preferedPhoneNumberDisplay: JSX.Element[];
        let otherPhoneNumbersDisplay: JSX.Element[];
        if (patient.Phones && patient.Phones.length > 0) {
            const prefered: PhoneNumber[] = patient.Phones.filter(x => x.IsPreferred);
            const others: PhoneNumber[] = patient.Phones.filter(x => !x.IsPreferred);

            if (prefered && prefered.length > 0) {
                preferedPhoneNumberDisplay = prefered.map((prefered, idx) => (
                    this.renderPhoneNumber(prefered, patient, idx)
                ));
            }

            if (others && others.length > 0) {
                otherPhoneNumbersDisplay = others.map((number, idx) => (
                    this.renderPhoneNumber(number, patient, idx)
                ));
            }
        }

        // adresses display
        let addressesDisplay: JSX.Element[];
        if (patient.Addresses && patient.Addresses.length > 0) {
            const addresses: Address[] = patient.Addresses.map(res => Address.FromResponseModel(res));

            addressesDisplay = addresses.map((add, idx) => (
                <div key={"address" + idx} className="patient-banner-address">
                    <li><span className="myle-icon-16 pin" /><span title={patient.getFullFormattedAddress()}>{patient.GetFormattedAddress(idx)}</span></li>
                    <li><span className="myle-icon-16 none" /><span>{add.State} {add.GetFormatedPostalCode()} {add.Country}</span></li>
                </div>
            ));
        }

        const genderLookup = cacheStore.GetLookupById(patient.IndividualResponse.GenderLookupId);

        const isBritishColumbiaPatient: boolean = this.props.isCurrentEmployeeActiveSiteInBritishColumbia && this.props.Model.get("Model").hasHealthCardInProvince(CanadaState.BritishColumbia);

        const showPopulationBasedFundingRegistrationPopupAsync = async (): Promise<void> => {
            if (await PopulationBasedFundingActions.showBillingLoginfNotLoggedInAsync(() => this.setState({ showPopulationBasedFundingRegistrationPopup: true })))
                return;

            this.setState({ showPopulationBasedFundingRegistrationPopup: true });
        };

        const onSubmitPopulationBasedFundingRegistration = async (formData: PopulationBasedFundingFormData): Promise<boolean> => {
            const response = await PopulationBasedFundingActions
                .registerPatient(patient, formData);

            if (response?.IsSuccess) {
                PopulationBasedFundingActions.pushEventToHistoryAndExtendItem(response.Event, patient);
                PopulationBasedFundingActions.updateIcon(patient.InactivableResponse.Id);

                this.setState({ ...this.state, showPopulationBasedFundingRegistrationPopup: false });
                sessionStore.dispatch(LocalPatientOperations.Profile.PatientBanner_ShowPopulationBasedFundingTab);
            }

            return response?.IsSuccess ?? false;
        };

        const redirectToPatientTabPBF = (): void => {
            sessionStore.dispatch(
                LocalOperations.Misc.RedirectToTab,
                {
                    target: RedirectTo.Patient,
                    value: patient.IndividualResponse.Contact_id,
                    currentLocation: RouterHistory.location,
                    navigateToSubTab: new HyperLinkRedirectControlPatientProfileSubTabs(ProfileTab.PopulationBasedFunding)
                }
            );
        };

        return (
            <div className="row patient-banner">
                {this.state.showPopulationBasedFundingRegistrationPopup && (
                    <PopulationBasedFundingRegistrationPopup
                        currentStarredPhysician={patient.HasDoctor() ? patient.GetAttendingDoctorOrSpecialistOnfileIntervenor()?.Employee.toPerson() : null}
                        onClose={() => { this.setState({ ...this.state, showPopulationBasedFundingRegistrationPopup: false }); }}
                        onTrySubmit={onSubmitPopulationBasedFundingRegistration}
                    />
                )}

                <div className="col-lg-12">
                    <div className="tb pateint-header">
                        <div className="tbc r0">
                            <div className="pull-left">
                                <div className="pic-profile-wrp">
                                    <img src={img_p_banner_icon_100} style={{ width: "48px" }} alt={patient.GetFormattedNameWithPrefix()} />

                                </div>
                                {this.props.onDropDownMenuClick && (
                                    <div className="patient-banner-dropdown">
                                        <span onClick={() => this.props.onDropDownMenuClick()} className="glyphicon glyphicon-triangle-bottom pointer" />
                                        {this.props.Model.get("PatientDropdownVisible") === true && (
                                            <PatientBannerDropdown
                                                Model={this.props.Model}
                                                shouldHideMemo={this.props.shouldHideMemo}
                                                shouldHideActivationLabel={this.props.shouldHideActivationLabel}
                                                shouldHideChangePicture={this.props.shouldHideChangePicture}
                                                shouldHideUpdateInformation={this.props.shouldHideUpdateInformation}
                                                onNewExternalCommunicationClick={() => this.props.onNewExternalCommunicationClick?.()}
                                                onDropDownMenuClose={() => this.props.onDropDownMenuClick()}
                                                onTaskOrMessageClick={(patient: Person, isTask: boolean) => this.props.onTaskOrMessageClick?.(patient, isTask)}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="tbc r1">
                            <div>
                                <HyperLinkRedirectControl target={RedirectTo.Patient} redirectValue={patient.IndividualResponse.Contact_id} text={patient.GetFormattedNameWithPrefix()} />

                                {genderLookup && genderLookup.LookupCode.toLowerCase() !== "othergender" && (
                                    <span className={`myle-icon-16 ${genderLookup.LookupCode.toLowerCase()}`} />
                                )}
                            </div>
                            <ul>
                                <li>
                                    <strong>{sessionStore.getDefaultHealthCardLabel()} </strong>
                                    <span>{patient.GetFormattedNAM()} {patient.GetFormattedNAMExpirationDateString()}</span>
                                    {cacheStore.ClinicSettings.EnableMedicalServicesPlanEligibility && isBritishColumbiaPatient && (
                                        <div className="inline-block margin-xs-left">
                                            <BritishColumbiaPatientBannerMspCoverage
                                                patient={new Patient(patient)}
                                                dateOfBirth={patient.IndividualResponse?.DateOfBirth}
                                            />
                                        </div>
                                    )}
                                </li>

                                <li><strong>{cacheStore.GetString("FileNumbersLabel")} </strong>
                                    <span className="patient-banner-file-number" title={patient.GetIdentifiers().toString()}>{patient.GetFormattedIdentifiers()}
                                    </span>
                                </li>
                                <li><strong>{cacheStore.GetString("DateofBirthLabel")} </strong><span>{patient.GetFormattedDateOfBirth() + patient.GetFormattedAge()}</span></li>
                            </ul>
                        </div>
                        <div className="tbc r2">
                            <ul>
                                {preferedPhoneNumberDisplay}
                                {otherPhoneNumbersDisplay}
                                {addressesDisplay}
                            </ul>
                        </div>
                        <div className="tbc r3">
                            <ul>
                                {!sessionStore.getCurrentEmployee().isActiveSiteMexico() && cacheStore.ClinicSettings.EnablePopulationBasedFunding && (
                                    <PopulationBasedFundingStatusController
                                        practician={patient.GetAttendingDoctorOrSpecialistOnfileIntervenor()}
                                        patientContactFacetId={patient.InactivableResponse.Id}
                                        onClickOpenRegistrationPopupActionAsync={async () => await showPopulationBasedFundingRegistrationPopupAsync()}
                                        onClickRedirectToPbfTabAction={() => redirectToPatientTabPBF()}
                                        showPracticianName={true}
                                    />
                                )}
                                {!sessionStore.getCurrentEmployee().isActiveSiteMexico() && !cacheStore.ClinicSettings.EnablePopulationBasedFunding && (
                                    <PatientBannerHealthCoverage
                                        patient={new Patient(patient)}
                                        site={sessionStore.getCurrentEmployee().getActiveSite()}
                                        clinicSettings={new ClinicSettings(cacheStore.ClinicSettings)}
                                        ontarioPatientEnrollmentModel={new OntarioPatientEnrollmentModel(patient.OntarioPatientEnrollmentModel)}
                                        onEnrollmentStatusClick={() => this.redirectToEnrollment()}
                                    />
                                )}
                                {lastAppointmentDisplay}
                                {upcomingAppointmentDisplay}
                                <li>
                                    <PreferredPharmacyStatus
                                        patientContactId={patient.IndividualResponse?.Contact_id}
                                        customPharmacy={patient.PreferredPharmacy?.CustomPharmacy}
                                        directoryPharmacyId={patient.PreferredPharmacy?.DirectoryPharmacyId}
                                        directoryPharmacy={patient.PreferredPharmacy?.getDirectoryPharmacy()}
                                    />
                                </li>
                            </ul>
                        </div>

                        <div className="tbc r4" />
                    </div>
                </div>
            </div>
        );
    }

    private async loadLastUpcomingAppointment(): Promise<void> {
        if (!this.props.shouldLoadLastUpcomingAppointment) {
            return;
        }

        const patientContactId = this.props.Model?.get("Model")?.IndividualResponse?.Contact_id;

        if (!patientContactId) {
            return;
        }

        const lastUpcomingAppointment: UpcomingAppointment = await getPatientLastAppointment(
            patientContactId
        );
        this.setState(p => ({ ...p, lastUpcomingAppointment }));
    }

    private async loadNextUpcomingAppointment(): Promise<void> {
        const nextUpcomingAppointment = this.props.Model?.get("Model")?.UpcomingAppointment;
        if (!nextUpcomingAppointment?.AppointmentId) {
            this.setState(p => ({ ...p, nextUpcomingAppointment: null }));
            return;
        }

        this.setState(
            {
                nextUpcomingAppointment: {
                    ...nextUpcomingAppointment
                }
            }
        );
    }

    private loadUpcomingAppointments(): void {
        this.loadLastUpcomingAppointment();
        this.loadNextUpcomingAppointment();
    }

    private async getOntarioEnrollmentStatus(): Promise<void> {
        const patient = this.props.Model.get("Model");

        const result = await Client.postAsync<{ PatientEnrollment: OntarioPatientEnrollmentModel }>(
            ServerOperations.Server.Myle_OntarioPatientEnrollment_GetEnrollmentForStatus,
            { PatientId: patient.InactivableResponse.Id }
        );

        if (!result.isSuccess) {
            sessionStore.showPopup(result.errors);
            return;
        }

        sessionStore.dispatch(LocalOperations.LabsUI.SetOntarioEnrollmentPatientBanner, { enrollmentStatus: result.data?.PatientEnrollment });
    }

    private redirectToEnrollment(): void {
        sessionStore.dispatch(LocalOperations.Misc.RedirectToTab, {
            currentLocation: RouterHistory.location,
            value: this.props.Model.get("Model").IndividualResponse.Contact_id,
            target: RedirectTo.Patient,
            navigateToSubTab: new HyperLinkRedirectControlPatientProfileSubTabs(ProfileTab.Enrollment)
        });
    }

    private renderPhoneNumber(phoneNumber: PhoneNumber, patient: Patient, phoneIndex: number): JSX.Element {
        let phoneIcon = null;

        if (patient)
            phoneIcon = PhoneNumberHelper.getPhoneIconVariantByPatient(phoneNumber, patient);

        return (
            <li key={"pref" + phoneIndex} className="phone-list-item">
                <span className="myle-icon-16 phone" />
                <span>
                    {cacheStore.ClinicSettings.IsCallCenterEnabled()
                        && <ClickToDialViewPhoneNumber phoneNumber={phoneNumber} showPhoneType={false} viewMode={ClickToDialViewMode.Hyperlink} patientContactFacetId={patient.IndividualResponse.ContactFacetId} />
                    }
                    {!cacheStore.ClinicSettings.IsCallCenterEnabled()
                        && PhoneNumber.stringFormattedPhone(phoneNumber)
                    }
                </span>
                <span className="text-gray">
                    ({cacheStore.GetLocalizedValueByLookupId(phoneNumber.PhoneTypeLookupId)} {phoneNumber.Notes})
                </span>
                {phoneIcon && (
                    <span title={cacheStore.GetString(phoneIcon.tooltipLocalizationKey)} className="no-margin-icon">
                        <IconBadge size="sm" variant={phoneIcon.variant} />
                    </span>
                )}
            </li>
        );
    }
}

export { PatientBannerForLabs };
